import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { twJoin } from 'tailwind-merge';
import { ChevronDownIcon } from '@heroicons/react/16/solid';

import { ActivityProgressOrdering } from '@/data/services/activityProgressServices';
import ActivityProgress from '@/models/ActivityProgress';
import { OrderingKey } from '@/models/Filter';
import { Button, TableColumn } from '@/components/ui/daisy';

import {
  KlassCell,
  UnitCell,
} from '@/components/shared/pendencies/tables/cells';
import {
  ActivityCell,
  ActivityLessonCell,
  CorrectActionCell,
  SentAtCell,
  StudentCell,
} from '@/components/shared/pendencies/tables/cells/activities';

export type ActivityPendencyColumnKey =
  | 'activity'
  | 'lesson'
  | 'unit'
  | 'klass'
  | 'student'
  | 'sentAt'
  | 'actions';

type UseActivityPendencyColumnsParams = {
  ordering?: ActivityProgressOrdering;
  onToggleOrdering: (field: OrderingKey<ActivityProgressOrdering>) => void;
};

export default function useActivityPendencyColumns({
  ordering,
  onToggleOrdering,
}: UseActivityPendencyColumnsParams): TableColumn<
  ActivityProgress,
  ActivityPendencyColumnKey
>[] {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pendencies.activities',
  });

  return useMemo(
    () => [
      {
        key: 'activity',
        header: t('columns.activity'),
        Cell: ActivityCell,
        headerClassName: 'w-[19%]',
      },
      {
        key: 'lesson',
        header: t('columns.lesson'),
        Cell: ActivityLessonCell,
        headerClassName: 'w-[14%]',
      },
      {
        key: 'unit',
        header: t('columns.unit'),
        Cell: UnitCell,
        headerClassName: 'w-[12%]',
      },
      {
        key: 'klass',
        header: t('columns.klass'),
        Cell: KlassCell,
        headerClassName: 'w-[20%]',
      },
      {
        key: 'student',
        header: t('columns.student'),
        Cell: StudentCell,
        headerClassName: 'w-[18%]',
      },
      {
        key: 'sentAt',
        header: (
          <Button
            variant="ghost"
            size="sm"
            className="font-bold"
            onClick={() => onToggleOrdering('created_at')}
          >
            {t('columns.sentAt')}
            <ChevronDownIcon
              className={twJoin(
                'w-4 transition-transform',
                ordering === 'created_at' && 'rotate-180',
              )}
            />
          </Button>
        ),
        Cell: SentAtCell,
        headerClassName: 'w-[17%]',
        cellClassName: 'whitespace-nowrap',
      },
      {
        key: 'actions',
        header: t('columns.actions'),
        Cell: CorrectActionCell,
        headerClassName: 'w-32 whitespace-nowrap',
        cellClassName: 'w-32 whitespace-nowrap',
      },
    ],
    [t, ordering, onToggleOrdering],
  );
}
